/**
 * Kuratierte gpm_ti-Objekte (Titane + Silos) je Map — 1:1 portiert aus dem alten
 * UI (`legacy-php/bf2142admin/assets/app.js`, `STATIC_OBJ`).
 *
 * Die Welt-x/z stammen aus den BF2142 `GamePlayObjects.con`-Leveldateien und
 * liegen im GLEICHEN Koordinatenraum wie die Spieler-Positionen — also über die
 * bestehende `mapWorldToTacPoint`-Kalibrierung projizierbar. KEINE Fake-/
 * Schätzdaten: nur exakt übernommene Legacy-Werte.
 *
 * Im Titan-Modus haben diese kuratierten Objekte Vorrang vor `static_objects.json`
 * (genau wie im alten UI: `soHard` vor `_cps`). Maps ohne Eintrag fallen auf
 * `static_objects.json` zurück.
 */

import type { TacPoint } from "./points";
import { normalizeMapKey, teamName, staticIconPath, type ProjectFn } from "./static-objects";

export interface CuratedTitan {
  x: number;
  z: number;
  team: number;
  rot: number;
  t: string;
  name: string;
}

export interface CuratedSilo {
  x: number;
  z: number;
  n: number; // feste Silo-Nummer 1..5 (Icon mini_titanSilo_0n)
  t: string;
  name: string;
}

export interface CuratedTitanMap {
  titans: CuratedTitan[];
  silos: CuratedSilo[];
}

/** Legacy `STATIC_OBJ` (app.js). Erweiterbar: weitere Titan-Maps brauchen eigene GamePlayObjects.con-Daten. */
export const CURATED_TITAN_OBJECTS: Record<string, CuratedTitanMap> = {
  suez_canal: {
    titans: [
      { x: 601.019, z: -241.849, team: 1, rot: 90, t: "CPNAME_SC_Ti_Outpost", name: "Outpost" },
      { x: -423.621, z: 2.536, team: 2, rot: -90, t: "CPNAME_SC_Ti_InnerBridgeLock", name: "InnerBridge" },
    ],
    silos: [
      { x: 263.556, z: -181.114, n: 1, t: "CPNAME_SC_Ti_DryLake", name: "DryLake" },
      { x: -115.399, z: 16.068, n: 2, t: "CPNAME_SC_Ti_OuterBridgeLock", name: "OuterBridge" },
      { x: 358.391, z: 50.044, n: 3, t: "CPNAME_SC_Ti_OilStation", name: "OilStation" },
      { x: 68.369, z: 206.893, n: 4, t: "CPNAME_SC_Ti_OilDepot", name: "OilDepot" },
      { x: 69.663, z: -57.447, n: 5, t: "CPNAME_SC_Ti_TrashDump", name: "TrashDump" },
    ],
  },
};

/** Kuratierte Titan-Map per (normalisiertem) Namen, oder null. */
export function curatedTitanMap(mapName: string | null | undefined): CuratedTitanMap | null {
  const key = normalizeMapKey(mapName);
  return key ? CURATED_TITAN_OBJECTS[key] ?? null : null;
}

/**
 * Projiziert die kuratierten Titane + Silos in TAC-Punkte (gleiche Projektion
 * wie Spieler/`static_objects`). Out-of-bounds/keine Calib -> Punkt übersprungen.
 * Silos behalten ihre feste Nummer `n` (Icon mini_titanSilo_0n), Titane bekommen
 * `mini_titan` + Team + Rotation (`rot`).
 */
export function curatedToTacPoints(data: CuratedTitanMap, calib: unknown, project: ProjectFn): TacPoint[] {
  const out: TacPoint[] = [];

  data.titans.forEach((t, i) => {
    const xy = project(calib, { x: Number(t.x) || 0, z: Number(t.z) || 0 });
    if (!xy) return;
    const team = teamName(t.team);
    out.push({
      id: `titan-${normalizeMapKey(t.t) || i}`,
      label: t.name || "Titan",
      kind: "titan",
      x: xy.x,
      y: xy.y,
      team,
      flagName: t.name,
      order: i,
      enabled: true,
      icon: staticIconPath("titan", team) ?? undefined,
      ...(Number.isFinite(t.rot) ? { rot: t.rot } : {}),
    });
  });

  data.silos.forEach((s, i) => {
    const xy = project(calib, { x: Number(s.x) || 0, z: Number(s.z) || 0 });
    if (!xy) return;
    // feste Nummer n -> Icon-Index (n ist 1-basiert; staticIconPath erwartet 0-basiert).
    const icon = staticIconPath("silo", "neutral", (Number(s.n) || 1) - 1) ?? undefined;
    out.push({
      id: `silo-${normalizeMapKey(s.t) || i}`,
      label: s.name || `Silo ${s.n}`,
      kind: "silo",
      x: xy.x,
      y: xy.y,
      team: "neutral",
      flagName: s.name,
      order: i,
      enabled: true,
      ...(icon ? { icon } : {}),
    });
  });

  return out;
}
